<template>
	<div v-if="visible" class="fd-cats-root fd-drop-down-container" :class="extraClasses">
		<div v-if="filter" class="type-search">{{filter}}</div>
		<div class="fd-cats fd-input-like" v-bind="tabindexBinding(0, true)" @focus="editorFocus" @blur="editorBlur" @keydown="editorTyping" :class="clazz" :style="style" ref="catsEditor">
			<span v-if="showPlaceholder" class="fd-cats-placeholder">{{ ct.placeholder }}</span>
			<span v-else-if="emptySelection" class="fd-pills" style="visibility: hidden"><span class="fd-cat-tag fd-pill">no</span></span>
			<span class="fd-pills"><span class="fd-cat-tag fd-pill" v-for="tag in tags" :key="tag.index">{{ tag.title }}</span></span>
		</div>
		<drop-down class="fd-cats-drop-down" ref="dd" style="padding:8px" @dropclose="filter=''">
			<category-item v-for="c in linearizedCategoriesWithFilter" :key="c.id" :cat="c" :level="c.level" :value="valueOf(c.id)" @change="stateChange" :noremove="!canRemoveMore" :noadd="!canAddMore"></category-item>
		</drop-down>
	</div>
</template>

<script setup lang="ts">
import useEditor, {type EditorProps, editorProps} from "./Editor";
import type {Categories, Category} from "@t/list/Category";
import type {Cats, CatsWithList} from "@t/fd/editor/Cats";
import {computed, ref} from "vue";
import {injectFindCategory, injectFindList} from "../PartInjectKeys";
import CategoryItem from "./cats/CategoryItem.vue";
import DropDown from "./DropDown.vue";
import {FormList} from "@t/fd/layout/FormDefinition";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;

const ct = computed<Cats>(() => props.part as Cats);

const {visible, value, clazz, style, ro, tabindexBinding} = useEditor(props);
const dd = ref<InstanceType<typeof DropDown> | null>(null);
const filter = ref<string>("");
const catsEditor = ref<HTMLElement | null>(null);

const emptySelection = computed(() => tags.value.length === 0);
const singleSelection = computed(() => tags.value.length === 1);
const multiSelection = computed(() => tags.value.length > 1);
const showPlaceholder = computed(() => ct.value.placeholder && emptySelection.value);
const extraClasses = computed(() => {
	const c: string[] = [];
	if (ro.value) c.push("fd-cats-disabled");
	return c;
});

const findCategory = injectFindCategory();
const findList = injectFindList();

const canAddMore = computed(() => {
	if (typeof ct.value.max === "number" && ct.value.max >= 1) {
		if (typeof ct.value.min === "number" && ct.value.min === ct.value.max) return true;   // must allow adding and the current item will be automatically remove
		if (Array.isArray(value.value)) return value.value.length < ct.value.max;
		if (value.value == null) return true;
		return ct.value.max > 1;
	}
	return true;
});

const canRemoveMore = computed(() => {
	if (typeof ct.value.min === "number" && ct.value.min >= 0) {
		if (Array.isArray(value.value)) return value.value.length > ct.value.min;
		if (value.value == null) return false;
		return ct.value.min == 0;
	}
	return true;
});

function listBased(ct: Cats): ct is CatsWithList {
	return !!ct.list;
}

const categoriesWithExtraItems = computed<Categories>(() => {
	const cats:Categories = [];
	if (!listBased(ct.value)) {
		cats.push(...ct.value.prepend);
		if (typeof ct.value.cats === "string") {
			for (let c of findCategory(ct.value.cats).value) {
				cats.push(c);
			}
		} else cats.push(...ct.value.cats);
	}
	return cats;
});

const categories = computed<Categories>(() => {
	if (listBased(ct.value)) {
		// Categories are specified as a list and must be converted to the Categories format
		const cats: Categories = [];
		if (ct.value.prepend) {
			for (let li of ct.value.prepend) {
				cats.push({id: li.value, title: li.name});
			}
		}
		let list: FormList;
		if (typeof ct.value.list === "string") {
			list = findList(ct.value.list).value;
		} else {
			list = ct.value.list;
		}
		for (let li of list) {
			cats.push({id: li.value, title: li.name});
		}
		return cats;
	} else {
		if (ct.value.prepend) {
			// Categories has special extra items
			return categoriesWithExtraItems.value;
		} else {
			if (typeof ct.value.cats === "string") {
				return findCategory(ct.value.cats).value;
			}
			return ct.value.cats;
		}
	}
});

function valueOf(c: string | number): boolean {
	if (Array.isArray(value.value)) {
		for (let v of value.value) {
			if (v === c) {
				return true;
			}
		}
		return false;
	}
	return value.value === c;
}

function stateChange(citem: { id: number | string, state: boolean }) {
	if (citem.state) {
		if (!Array.isArray(value.value)) value.value = [];
		if (!value.value.find((i: string | number) => i === citem.id)) {
			if (typeof ct.value.max === "number" && ct.value.max >= 1) {
				// Check if this will excede max allowed
				while (value.value.length >= ct.value.max) value.value.splice(0, 1);
			}
			value.value.push(citem.id);
		}
	} else {
		if (!Array.isArray(value.value)) value.value = [];
		const idx = value.value.findIndex((i: string | number) => i === citem.id);
		if (idx >= 0) {
			if (typeof ct.value.min === "number" && ct.value.min >= 0) {
				if (value.value.length <= ct.value.min) return;
			}
			value.value.splice(idx, 1);
		}
	}
	if (catsEditor.value) {
		catsEditor.value.focus();
	}
}

function editorFocus() {
	if (dd.value != null && !ro.value) {
		dd.value.editorFocus(catsEditor);
	}
}

function editorBlur() {
	if (dd.value != null && !ro.value) {
		dd.value.editorBlur();
	}
}

function insertChar(ch: string, e: KeyboardEvent) {
	filter.value = filter.value + ch;
	if (filter.value.length > 14) filter.value = filter.value.substring(1);
	e.preventDefault();
}

function removeChar(e: KeyboardEvent) {
	if (filter.value.length > 0) filter.value = filter.value.slice(0, -1);
}

function clear(e: KeyboardEvent) {
	filter.value = "";
}

function editorTyping(e: KeyboardEvent) {
	const key = e.key;
	if (key) {
		if (key.length === 1 /* on char */) insertChar(key, e);
		else if (key === "Backspace") removeChar(e);
		else if (key === "Escape") clear(e);
	}
}

interface LinCategory extends Category {
	level: number
}

function appendLinCategory(lcs: LinCategory[], cs: Category[], level: number): void {
	for (let c of cs) {
		lcs.push({
			...c,
			level: level
		});
		if (c.children) appendLinCategory(lcs, c.children, level + 1);
	}
}

const linearizedCategories = computed<LinCategory[]>(() => {
	let lcs: LinCategory[] = [];
	appendLinCategory(lcs, categories.value, 0);
	return lcs;
});

const linearizedCategoriesWithFilter = computed<LinCategory[]>(() => {
	let lcs: LinCategory[] = linearizedCategories.value;
	let f = filter.value;
	if (f.length > 0) {
		f = f.toLowerCase();
		const filtered: LinCategory[] = [];
		for (let lc of lcs) {
			if (Array.isArray(lc.children) && lc.children.length > 0) {
				filtered.push(lc);
			} else if (lc.title.toLowerCase().indexOf(f) >= 0) filtered.push(lc);
		}
		lcs = filtered;
	}
	return lcs;
});

interface CatTag {
	title: string,
	index: number,
}

function selectedIndex(id: string | number): number {
	if (Array.isArray(value.value)) {
		return value.value.findIndex((i: string | number) => i === id);
	}
	return value.value === id ? 0 : -1;
}

const tags = computed<CatTag[]>(() => {
	const t: CatTag[] = [];
	for (let lc of linearizedCategories.value) {
		const idx = selectedIndex(lc.id);
		if (idx >= 0) {
			t.push({title: lc.title, index: idx});
		}
	}
	return t;
});
</script>

<style>
.fd-cats-root {
	position: relative;
}
.fd-cats {
	padding: 0;
	min-height: var(--input-height);
	background-color: var(--bg);
	color: var(--fg);
	display: flex;
	flex-direction: row;
	align-items: baseline;

	margin: 0;
	font-size: var(--input-font-size);
	line-height: var(--input-height);
	border-radius: var(--input-radius);
	font-family: var(--font);
	cursor: default;
	box-shadow: var(--input-offset-shadow);

	&.fd-input-like {
		padding-inline: 0;
	}

	&:focus-visible,
	&:focus {
		outline: none;
		box-shadow: 0 0 0 1px var(--input-bg), 0 0 2px 3px var(--outline-color);
	}
}

.fd-cats-placeholder {
	color: var(--placeholder-fg);
	padding-right: 8px;
	padding-left: var(--input-h-padding);
}

.fd-cat-tag-single {
}

.fd-cats-disabled {
	& > .fd-cats {
		color: var(--input-disabled-fg);
		background-color: var(--input-disabled-bg);
		box-shadow: var(--input-disabled-offset-shadow);

		& .fd-pill {
			background-color: var(--pill-disabled-bg);
			color: var(--pill-disabled-fg);
			box-shadow: var(--pill-disabled-shadow);
		}
	}
}

.fd-cat-tag {
}


/* Sequence of pills or tags */
.fd-pills {
	display: flex;
	justify-content: flex-start;
	align-items: baseline;
	flex-wrap: wrap;
	column-gap: var(--input-h-padding);
	row-gap: 1px;
	line-height: var(--input-height);
	overflow-y: auto;
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
	min-width: 80px;
	user-select: none;
	-webkit-user-select: none;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.fd-pills::-webkit-scrollbar {
	display: none;
}

.fd-pill {
	background-color: var(--pill-bg);
	color: var(--pill-fg);
	box-shadow: var(--pill-shadow);
	border-radius: var(--pill-radius);
	padding-inline: var(--input-h-padding);
	line-height: var(--input-height);
	font-weight: normal;
	display: inline-block;

	&:hover {
		background-color: var(--pill-bg-hover);
	}
}
</style>
